// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useEffect, useRef, useState } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { shouldPulse, nextPrevSignal, BEAT_MIN_INTERVAL_MS } from '../utils/heartbeat';

// Tiny ECG heartbeat for device tiles, three visual states:
//   inactive — dim grey static flatline (layout stays stable when offline)
//   active   — a glowing green dot travels the trace continuously, dragging
//              a fading tail (comet scan), so a live device is unmistakable
//              at a glance (a beat-only design read as "static" 98% of the
//              time at 60s check-in cadence)
//   beat     — when `signal` CHANGES while `active`, a bright copy of the
//              trace draws across once (via key-remount of the sweep
//              polyline — re-adding a CSS class does NOT restart an
//              animation, and this page re-renders every few seconds from
//              the MQTT activity tick).
//
// Mount guard: the previous-signal ref is initialised to the FIRST observed
// signal, so navigating to the page never fires every tile at once
// (activityMap is hydrated from localStorage before first render).
// Burst guard: beats closer than BEAT_MIN_INTERVAL_MS collapse into one —
// a stargate publish cycle lands status+health+metrics within ~a second.

const TRACE = '1,10 12,10 16,10 18,3 20,13 22,10 27,10 43,10';
// Same geometry as TRACE, as a path string for CSS offset-path (the glowing
// dot travels this). Keep the two in lockstep if the trace ever changes.
const TRACE_PATH = 'M1 10 L12 10 L16 10 L18 3 L20 13 L22 10 L27 10 L43 10';

// Scan loop duration — must match the ecg-travel/ecg-scan animation-duration
// in index.css. The tail layers are phase-shifted by their dash length so
// their LEADING edges ride exactly under the dot (a dash segment of length L
// leads an offset-path dot of the same phase by L% of the loop).
const SCAN_DURATION_S = 2.6;

/** Cheap stable hash of the signal string for scan-phase spreading. */
function scanPhaseFrom(signal) {
  const s = String(signal || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) % 1000;
  }
  return h;
}

const HeartbeatECG = ({ signal, active, title, style }) => {
  const prevSignalRef = useRef(signal);
  const lastBeatAtRef = useRef(0);
  const scanPhaseRef = useRef(scanPhaseFrom(signal));
  const [beatSeq, setBeatSeq] = useState(0);

  useEffect(() => {
    if (shouldPulse({ prevSignal: prevSignalRef.current, signal, active })) {
      const now = Date.now();
      if (now - lastBeatAtRef.current >= BEAT_MIN_INTERVAL_MS) {
        lastBeatAtRef.current = now;
        setBeatSeq((s) => s + 1);
      }
    }
    // Frozen while inactive (see nextPrevSignal) — advancing here would
    // swallow the recovery beat that arrives before the poll flips `active`.
    prevSignalRef.current = nextPrevSignal({
      prevSignal: prevSignalRef.current,
      signal,
      active,
    });
  }, [signal, active]);

  const svg = (
    <svg
      className="ecg-svg"
      width="44"
      height="14"
      viewBox="0 0 44 14"
      aria-hidden="true"
      focusable="false"
    >
      <polyline
        className="ecg-base"
        points={TRACE}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {/* Comet scan: a glowing dot (core + soft halo — two circles, no blur
          filters) travels the trace via CSS offset-path, dragging a fading
          tail built from two dash layers of decreasing length/opacity. Each
          tail layer is retarded by (dashLength% × duration) so its leading
          edge stays exactly under the dot. The shared negative base delay
          de-phases tiles (all mount in the same commit — without it the
          whole grid marches in lockstep); frozen per mount (ref) so a beat
          doesn't re-phase a running scan. */}
      {active &&
        (() => {
          const base = -((scanPhaseRef.current % 26) / 10);
          // Subtract one full period so every delay stays NEGATIVE — a
          // positive delay would leave that layer invisible until it elapses
          // on mount. Phase is unchanged (mod duration). animationDuration is
          // set inline on every layer so SCAN_DURATION_S is the SINGLE source
          // of truth — retuning only the CSS 2.6s would silently detach the
          // tail from the dot.
          const duration = `${SCAN_DURATION_S}s`;
          const delayFor = (dashLen) => ({
            animationDuration: duration,
            animationDelay: `${(base + (dashLen / 100) * SCAN_DURATION_S - SCAN_DURATION_S).toFixed(3)}s`,
          });
          const pathStyle = { offsetPath: `path("${TRACE_PATH}")` };
          return (
            <>
              <polyline
                className="ecg-tail ecg-tail-outer"
                points={TRACE}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                pathLength="100"
                style={delayFor(12)}
              />
              <polyline
                className="ecg-tail ecg-tail-inner"
                points={TRACE}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
                strokeLinecap="round"
                pathLength="100"
                style={delayFor(5)}
              />
              <circle
                className="ecg-dot-halo"
                r="3"
                fill="currentColor"
                style={{ ...pathStyle, ...delayFor(0) }}
              />
              <circle
                className="ecg-dot-core"
                r="1.5"
                style={{ ...pathStyle, ...delayFor(0) }}
              />
            </>
          );
        })()}
      {beatSeq > 0 && (
        <polyline
          key={beatSeq}
          className="ecg-sweep"
          points={TRACE}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          pathLength="100"
        />
      )}
    </svg>
  );

  // Tooltip wraps the SPAN, not the raw svg: an svg root only hit-tests its
  // painted strokes (~1.5px lines), which would make the tooltip effectively
  // unreachable. Alignment (e.g. marginLeft:'auto') is the call site's job
  // via `style`.
  const wrap = (
    <span
      className={`ecg-wrap${active ? '' : ' ecg-inactive'}`}
      style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', ...style }}
    >
      {svg}
    </span>
  );

  return title ? (
    <Tooltip title={title} placement="top" arrow>
      {wrap}
    </Tooltip>
  ) : (
    wrap
  );
};

export default HeartbeatECG;
